import type { UserDto } from './user.dto';
import type { AssignedSurveyDto } from './surveys/lib/assigned-survey.dto';
import { z } from 'zod';

export interface HospitalizationUpdateDto {
  note?: string;
  requiresFollowUp?: boolean;
}

export const CreateHospitalizationRequestDtoSchema = z.object({
  memberId: z.string().min(1, 'Member ID is required'),
  hospital: z.string().optional(),
  hospitalInfo: z.string().optional(),
  note: z.string().optional(),
  // Admit
  admitComplaint: z.string().optional(),
  admitNotificationTime: z.string().optional(),
  admitType: z.string().optional(),
  admitDate: z.string().optional(),
  // Discharge
  dischargeNotificationTime: z.string().optional(),
  dischargeDate: z.string().optional(),
  dischargeDisposition: z.string().optional(),
  // Follow Up
  followUpDate: z.string().optional(),
  followUpType: z.string().optional(),
  requiresFollowUp: z.boolean().optional(),
  expectedSurveyCompletion: z.string().optional(),
  isEnrolledInBehavioralHealth: z.boolean().optional(),
  isAssumed: z.boolean().optional(),
  hospitalizationEventId: z.string().optional(),
});

export type CreateHospitalizationRequestDto = z.infer<
  typeof CreateHospitalizationRequestDtoSchema
>;
export interface HospitalizationDto {
  id: number;
  note: string;

  memberId: string;
  hospital: string;
  hospitalInfo: string;
  // Admit
  admitComplaint: string;
  admitNotificationTime: string;
  admitType: string;
  admitDate: string;
  // Discharge
  dischargeNotificationTime: string;
  dischargeDate: string;
  dischargeDisposition: string;
  // Follow Up
  followUpDate: string;
  followUpType: string;
  requiresFollowUp: boolean;
  expectedSurveyCompletion: string;
  assignedSurveyId: number;

  // Tracking Timestamps
  attemptedAt: number;
  offeredAt: number;

  member: UserDto;
  isEnrolledInBehavioralHealth: boolean;
  isAssumed: boolean;
}

export interface HospitalizationFollowUpCountsDto {
  lastUpdatedAt: number;
  notAttempted: number;
  notOffered: number;
}

export interface CurrentAndPendingHospitalizationsDto {
  meta: {
    total: number;
    offeredCount: number;
    attemptedCount: number;
  };
  data: Record<string, number[]>;
  hospitalizations: HospitalizationDto[];
  allHospitalizations: HospitalizationDto[];
  assignedSurveys: AssignedSurveyDto[];
}

export const HospitalizationFormSchema = z
  .object({
    note: z.string().refine(
      value => {
        // Remove HTML tags and check if empty
        const plainText = value.replace(/<[^>]*>/g, '');
        const trimmed = plainText.trim();
        const isEmptyHtml =
          trimmed === '' || trimmed === null || trimmed === undefined;
        return !isEmptyHtml;
      },
      {
        message: 'Note is required',
      }
    ),
  })
  .strict();
export type HospitalizationForm = z.infer<typeof HospitalizationFormSchema>;
